import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { apiClient, fileUrl } from '@/lib/api';
import { downscaleImage } from '@/lib/image';
import { PhoneInput } from '@/components/PhoneInput';
import {
  useAddCompanyWebsite,
  useCompany,
  useCompanyWebsites,
  useDeactivateUser,
  useRemoveCompanyWebsite,
  useUpdateCompany,
  useUsers,
} from '@/lib/adminApi';
import { useQueryClient } from '@tanstack/react-query';
import type { CompanySummary, UserAdminSummary, RoleSchemaType } from '@ventin/shared';
import { ClearCacheButton } from '@/components/websites/ClearCacheButton';
import { AgreementCard } from './AgreementCard';
import { UserFormDialog } from './UserFormDialog';

/** American time zones only (owner spec 2026-06-11) */
const US_TIMEZONES = [
  { tz: 'America/New_York', label: 'Eastern (ET)' },
  { tz: 'America/Chicago', label: 'Central (CT)' },
  { tz: 'America/Denver', label: 'Mountain (MT)' },
  { tz: 'America/Phoenix', label: 'Arizona (no DST)' },
  { tz: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { tz: 'America/Anchorage', label: 'Alaska (AKT)' },
  { tz: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
];

/**
 * CompanyDetailPage — /admin/companies/:id, the company profile.
 *
 * Owner spec 2026-06-11: every card edits in place via a pencil icon (no
 * modal). Header card owns the wide logo + friendly name + slug; the business
 * card holds contact/address/timezone with legal name + EIN at the bottom;
 * Websites sits above the agreement; the agreement edits inline with
 * plain-English labels and links to a website.
 */
export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useCompany(id);
  const company = data?.company ?? null;

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <p className="text-sm text-muted-foreground">Loading company…</p>
      </div>
    );
  }

  if (isError || !company || !id) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Link to="/admin/companies" className="text-sm text-muted-foreground hover:underline">
          ← Back to Companies
        </Link>
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>Company not found.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <Link to="/admin/companies" className="text-sm text-muted-foreground hover:underline">
        ← Back to Companies
      </Link>

      <HeaderCard company={company} />
      <BusinessCard company={company} />
      <WebsitesCard companyId={id} />
      <AgreementCard companyId={id} />
      <UsersSection companyId={id} />
    </div>
  );
}

// --- Header card: wide logo + name + slug -----------------------------------

function HeaderCard({ company }: { company: CompanySummary }) {
  const updateMut = useUpdateCompany();
  const queryClient = useQueryClient();
  const logoRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(company.name);
  const [slug, setSlug] = useState(company.slug);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(company.name);
    setSlug(company.slug);
  }, [company]);

  async function uploadLogo(file: File) {
    setUploadingLogo(true);
    try {
      const image = await downscaleImage(file, 1024);
      await apiClient.put(`/api/admin/companies/${company.id}/logo`, image, {
        headers: { 'Content-Type': image.type || file.type },
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      toast.success('Logo updated');
    } catch {
      toast.error('Logo must be a PNG, JPEG, or WebP image');
    } finally {
      setUploadingLogo(false);
    }
  }

  async function save() {
    setBusy(true);
    try {
      await updateMut.mutateAsync({ id: company.id, patch: { name, slug } });
      toast.success('Saved');
      setEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      toast.error(/slug/i.test(message) ? 'Slug already in use' : "Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="relative mt-3 mb-6 rounded-md border bg-background p-4">
      {!editing && (
        <button
          type="button"
          aria-label="Edit name and slug"
          title="Edit name and slug"
          className="absolute right-3 top-3 p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted"
          onClick={() => setEditing(true)}
        >
          <Pencil className="h-4 w-4" />
        </button>
      )}
      <div className="flex items-center gap-5">
        <div className="flex flex-col items-center gap-1.5">
          {company.logoPath ? (
            <img
              src={fileUrl(company.logoPath)}
              alt={`${company.name} logo`}
              className="h-12 max-w-52 object-contain"
            />
          ) : (
            <div className="h-12 w-24 rounded-md bg-primary/10 flex items-center justify-center text-primary font-semibold">
              {company.name.slice(0, 2).toUpperCase()}
            </div>
          )}
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-primary hover:underline"
            disabled={uploadingLogo}
            onClick={() => logoRef.current?.click()}
          >
            {uploadingLogo ? 'Uploading…' : company.logoPath ? 'Change logo' : 'Upload logo'}
          </button>
          <input
            ref={logoRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void uploadLogo(f);
              e.target.value = '';
            }}
          />
        </div>

        {!editing ? (
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-semibold leading-[1.3]">{company.name}</h2>
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-[10px] py-1 text-[12px] font-medium',
                  company.isArchived
                    ? 'bg-[#F3F4F6] text-[#6B7280]'
                    : 'bg-[#ECFDF5] text-[#16A34A]',
                )}
              >
                {company.isArchived ? 'Archived' : 'Active'}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {company.slug}
              {company.niche && ` · ${company.niche}`}
            </p>
          </div>
        ) : (
          <div className="flex-1 max-w-md space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="hdr-name">Business name</Label>
              <Input id="hdr-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hdr-slug">Slug (URL ID)</Label>
              <Input id="hdr-slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// --- Business profile card (inline edit) -------------------------------------

function BusinessCard({ company }: { company: CompanySummary }) {
  const updateMut = useUpdateCompany();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [niche, setNiche] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [timezone, setTimezone] = useState('');
  const [legalName, setLegalName] = useState('');
  const [ein, setEin] = useState('');

  useEffect(() => {
    setEmail(company.email ?? '');
    setPhone(company.phone ?? '');
    setWebsite(company.website ?? '');
    setNiche(company.niche ?? '');
    setAddressLine1(company.addressLine1 ?? '');
    setAddressLine2(company.addressLine2 ?? '');
    setCity(company.city ?? '');
    setState(company.state ?? '');
    setZip(company.zip ?? '');
    setTimezone(company.timezone ?? '');
    setLegalName(company.legalName ?? '');
    setEin(company.ein ?? '');
  }, [company, editing]);

  async function save() {
    setBusy(true);
    try {
      const v = (s: string) => s.trim() || null;
      await updateMut.mutateAsync({
        id: company.id,
        patch: {
          email: v(email),
          phone: v(phone),
          website: v(website),
          niche: v(niche),
          addressLine1: v(addressLine1),
          addressLine2: v(addressLine2),
          city: v(city),
          state: v(state),
          zip: v(zip),
          timezone: v(timezone),
          legalName: v(legalName),
          ein: v(ein),
        },
      });
      toast.success('Saved');
      setEditing(false);
    } catch {
      toast.error("Couldn't save — check the fields and try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="relative mb-6 rounded-md border bg-background p-4">
      {!editing && (
        <button
          type="button"
          aria-label="Edit business info"
          title="Edit business info"
          className="absolute right-3 top-3 p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted"
          onClick={() => setEditing(true)}
        >
          <Pencil className="h-4 w-4" />
        </button>
      )}

      {!editing ? (
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
          <Field label="Email" value={company.email} />
          <Field label="Phone" value={company.phone} />
          <Field
            label="Website"
            value={company.website}
            render={(v) => (
              <a
                href={v.startsWith('http') ? v : `https://${v}`}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {v}
              </a>
            )}
          />
          <Field label="Niche" value={company.niche} />
          <Field
            label="Time zone"
            value={company.timezone}
            render={(v) => <>{US_TIMEZONES.find((z) => z.tz === v)?.label ?? v}</>}
          />
          <div className="sm:col-span-2 lg:col-span-3">
            <dt className="text-muted-foreground">Address</dt>
            <dd className="font-medium">
              {[
                company.addressLine1,
                company.addressLine2,
                [company.city, company.state, company.zip].filter(Boolean).join(', '),
                company.country,
              ]
                .filter(Boolean)
                .join(' · ') || '—'}
            </dd>
          </div>
          <Field label="Legal name" value={company.legalName} />
          <Field label="EIN" value={company.ein} />
        </dl>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="biz-email">Business email</Label>
              <Input
                id="biz-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-phone">Business phone</Label>
              <PhoneInput id="biz-phone" value={phone} onChange={setPhone} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-website">Website (informational)</Label>
              <Input
                id="biz-website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-niche">Niche / industry</Label>
              <Input id="biz-niche" value={niche} onChange={(e) => setNiche(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="biz-addr1">Street address</Label>
            <Input
              id="biz-addr1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="biz-addr2">Street address 2 (suite, unit…)</Label>
            <Input
              id="biz-addr2"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="biz-city">City</Label>
              <Input id="biz-city" value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-state">State</Label>
              <Input id="biz-state" value={state} onChange={(e) => setState(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-zip">ZIP code</Label>
              <Input id="biz-zip" value={zip} onChange={(e) => setZip(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5 max-w-sm">
            <Label htmlFor="biz-tz">Time zone</Label>
            <select
              id="biz-tz"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              <option value="">Not set</option>
              {US_TIMEZONES.map(({ tz, label }) => (
                <option key={tz} value={tz}>
                  {label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Times are stored in UTC and shown in this zone for the company.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t">
            <div className="space-y-1.5">
              <Label htmlFor="biz-legal">Legal business name</Label>
              <Input
                id="biz-legal"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-ein">EIN (optional)</Label>
              <Input
                id="biz-ein"
                value={ein}
                onChange={(e) => setEin(e.target.value)}
                placeholder="XX-XXXXXXX"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// --- Websites card ------------------------------------------------------------

function WebsitesCard({ companyId }: { companyId: string }) {
  const { data } = useCompanyWebsites(companyId);
  const addMut = useAddCompanyWebsite();
  const removeMut = useRemoveCompanyWebsite();
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [mainwpSites, setMainwpSites] = useState<
    { id: string; name: string; url: string }[] | null
  >(null);
  const [mainwpBusy, setMainwpBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const websites = data?.websites ?? [];
  const normalize = (u: string) =>
    u
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '')
      .toLowerCase();
  const existing = new Set(websites.map((w) => normalize(w.url)));

  async function loadMainwp() {
    setMainwpBusy(true);
    try {
      const res = await apiFetchSites();
      setMainwpSites(res);
      setSelected(new Set());
    } catch (err) {
      const unavailable = isAxiosError(err) && err.response?.status === 503;
      toast.error(
        unavailable
          ? 'MainWP is not connected yet — set MAINWP_BASE_URL and MAINWP_API_KEY'
          : "Couldn't reach MainWP — check the dashboard API key",
      );
    } finally {
      setMainwpBusy(false);
    }
  }

  async function importSelected() {
    if (!mainwpSites) return;
    const picks = mainwpSites.filter((s) => selected.has(s.id));
    for (const site of picks) {
      await addMut
        .mutateAsync({ companyId, input: { url: site.url, label: site.name } })
        .catch(() => toast.error(`Couldn't add ${site.url}`));
    }
    if (picks.length) toast.success(`Imported ${picks.length} site${picks.length > 1 ? 's' : ''}`);
    setMainwpSites(null);
  }

  async function add() {
    if (!url.trim()) return;
    try {
      await addMut.mutateAsync({
        companyId,
        input: { url: url.trim(), ...(label.trim() ? { label: label.trim() } : {}) },
      });
      setUrl('');
      setLabel('');
      toast.success('Website added');
    } catch {
      toast.error("Couldn't add website — try again");
    }
  }

  return (
    <section className="mb-8">
      <h3 className="text-sm font-medium uppercase text-muted-foreground mb-1">Websites</h3>
      <p className="text-xs text-muted-foreground mb-2">
        The sites we support for this company. With multiple sites, link each agreement to its
        website below.
      </p>
      <div className="rounded-md border bg-background divide-y">
        {websites.length === 0 && (
          <p className="px-4 py-4 text-sm text-muted-foreground">No websites yet.</p>
        )}
        {websites.map((w) => (
          <div key={w.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
            <a
              href={w.url.startsWith('http') ? w.url : `https://${w.url}`}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary hover:underline"
            >
              {w.url}
            </a>
            {w.label && <span className="text-muted-foreground">{w.label}</span>}
            <div className="ml-auto flex items-center gap-2">
              {w.mainwpSiteId && (
                <ClearCacheButton
                  websiteId={w.id}
                  companyId={companyId}
                  cacheLastClearedAt={w.cacheLastClearedAt}
                />
              )}
              <button
                type="button"
                aria-label={`Remove ${w.url}`}
                title="Remove website"
                className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted"
                disabled={removeMut.isPending}
                onClick={() => {
                  void removeMut.mutateAsync({ companyId, websiteId: w.id });
                }}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        <div className="px-4 py-3 flex flex-col sm:flex-row gap-2">
          <Input
            aria-label="Website address"
            placeholder="example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Input
            aria-label="Label (optional)"
            placeholder="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="sm:max-w-48"
          />
          <Button onClick={add} disabled={addMut.isPending || !url.trim()}>
            Add website
          </Button>
          <Button variant="outline" onClick={loadMainwp} disabled={mainwpBusy}>
            {mainwpBusy ? 'Loading…' : 'Import from MainWP'}
          </Button>
        </div>

        {mainwpSites && (
          <div className="px-4 py-3 space-y-2">
            {mainwpSites.length === 0 && (
              <p className="text-sm text-muted-foreground">No sites found on MainWP.</p>
            )}
            {mainwpSites.map((site) => {
              const already = existing.has(normalize(site.url));
              return (
                <label
                  key={site.id}
                  className={cn(
                    'flex items-center gap-2 text-sm',
                    already && 'text-muted-foreground',
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-[#1924a2]"
                    disabled={already}
                    checked={already || selected.has(site.id)}
                    onChange={(e) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(site.id);
                        else next.delete(site.id);
                        return next;
                      });
                    }}
                  />
                  <span className="font-medium">{site.name}</span>
                  <span className="text-muted-foreground">{site.url}</span>
                  {already && <span className="text-xs">(already added)</span>}
                </label>
              );
            })}
            {mainwpSites.length > 0 && (
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={importSelected} disabled={selected.size === 0}>
                  Add selected ({selected.size})
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setMainwpSites(null)}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

async function apiFetchSites(): Promise<{ id: string; name: string; url: string }[]> {
  const res = await apiClient.get<{ sites: { id: string; name: string; url: string }[] }>(
    '/api/admin/integrations/mainwp/sites',
  );
  return res.data.sites;
}

// --- Users section -------------------------------------------------------------

function UsersSection({ companyId }: { companyId: string }) {
  const { data: usersData } = useUsers({ companyId });
  const deactivateMut = useDeactivateUser();
  const [userFormMode, setUserFormMode] = useState<'create' | 'edit' | null>(null);
  const [editingUser, setEditingUser] = useState<UserAdminSummary | undefined>(undefined);
  const users = useMemo(() => usersData?.users ?? [], [usersData]);

  async function handleDeactivateUser(user: UserAdminSummary) {
    try {
      await deactivateMut.mutateAsync({ id: user.id });
      toast.success('User deactivated');
    } catch {
      toast.error("Couldn't save — try again");
    }
  }

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium uppercase text-muted-foreground">Users</h3>
        <Button
          size="sm"
          onClick={() => {
            setEditingUser(undefined);
            setUserFormMode('create');
          }}
        >
          Add user
        </Button>
      </div>
      <div className="rounded-md border bg-background overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last login</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No users yet — add the company's first user.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className="border-t">
                <td className="px-4 py-3 font-medium">{u.fullName}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  <RoleBadge role={u.role} />
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {u.isActive ? 'Active' : 'Deactivated'}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingUser(u);
                        setUserFormMode('edit');
                      }}
                      aria-label={`Edit ${u.fullName}`}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeactivateUser(u)}
                      disabled={deactivateMut.isPending || !u.isActive}
                      aria-label={`Deactivate ${u.fullName}`}
                    >
                      Deactivate
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserFormDialog
        mode={userFormMode ?? 'create'}
        initial={editingUser}
        defaultCompanyId={companyId}
        open={userFormMode !== null}
        onOpenChange={(o) => {
          if (!o) setUserFormMode(null);
        }}
      />
    </section>
  );
}

function Field({
  label,
  value,
  render,
}: {
  label: string;
  value: string | null;
  render?: (v: string) => React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value ? (render ? render(value) : value) : '—'}</dd>
    </div>
  );
}

function RoleBadge({ role }: { role: RoleSchemaType }) {
  const tone =
    role === 'ADMIN'
      ? 'bg-[#FEF3C7] text-[#92400E]'
      : role === 'SUPPORT'
        ? 'bg-[#DBEAFE] text-[#1E40AF]'
        : 'bg-[#ECFDF5] text-[#16A34A]';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-[10px] py-1 text-[12px] font-medium',
        tone,
      )}
    >
      {role}
    </span>
  );
}
